import { defineStore } from 'pinia'
import { ref } from 'vue'
import { Team, TeamMap } from '@/types/team'
import { teamService } from '@/services/teamService'
import { useSessionStore } from './sessionStore'

export const useTeamStore = defineStore(
  'team',
  () => {
    // State
    const teams = ref<TeamMap>({})
    const currentTeam = ref<Team | null>(null)

    // Actions
    function setTeams(teamArray: Team[]) {
      teams.value = teamArray.reduce((map: TeamMap, team: Team) => {
        map[team.id] = team
        return map
      }, {} as TeamMap)
    }

    async function setCurrentTeam(team: Team | null) {
      currentTeam.value = team

      // Update session store if current session's teamId does not match
      const sessionStore = useSessionStore()
      if (team) {
        await sessionStore.loadSessionsByTeamIdFromApi(team.id)
      } 
    }

    function addTeam(team: Team) {
      teams.value[team.id] = team
    }

    function updateTeam(updatedTeam: Team) {
      teams.value[updatedTeam.id] = updatedTeam
    }

    function removeTeam(teamId: string) {
      delete teams.value[teamId]
      if (currentTeam.value?.id === teamId) {
        currentTeam.value = null
      }
    }

    async function loadTeamsFromApi(teamIds: string[]): Promise<void> {
      try {
        const fetchedTeams = await teamService.fetchByIDs(teamIds)
        if (fetchedTeams && fetchedTeams.length > 0) {
          setTeams(fetchedTeams)
          
          // Preserve the selected team if it still exists in the newly fetched list
          const preservedTeam = currentTeam.value && fetchedTeams.find(t => t.id === currentTeam.value!.id)
          await setCurrentTeam(preservedTeam || fetchedTeams[0])
        } else if (fetchedTeams) {
          setTeams([])
          await setCurrentTeam(null)
        }
      } catch (error) {
        console.error('Failed to load teams from API:', error)
      }
    }

    return {
      teams,
      currentTeam,
      setTeams,
      setCurrentTeam,
      addTeam,
      updateTeam,
      removeTeam,
      loadTeamsFromApi
    }
  },
  {
    persist: {
      key: 'teamStore',
      storage: localStorage,
    },
  },
)
